import React from "react";
import useSiteContent from "@/lib/useSiteContent";
import { platformMediaSrc } from "../../lib/platformMedia";
import { BOOSTER_ICONS, FALLBACK_BOOSTER_ICON, formatPrice } from "./_shared";
import PackageCta from "./PackageCta";

/**
 * Boosters (recurring add-ons, each actionable) + platform fees (rates, nothing to buy).
 *
 * A booster card gets a `PackageCta` for the same reason the install/porting cards do
 * (BUG-206): it advertises a monthly price and used to expose no action at all. The
 * platform-fee tiles deliberately keep none — a fee is not a purchasable item, it is the
 * rate applied to sales the practice makes.
 *
 * A booster with an image (`image_url`, chosen in the Super Admin booster editor) shows it
 * in place of its icon.
 */
export default function BoostersAndFees({ boosters, platformFees, currency }) {
  const { text } = useSiteContent();
  return (
    <section className="py-28 bg-gradient-to-b from-amber-50/30 to-sol-ivory">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
        {/* Boosters */}
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-sol-gold mb-4">{text("landing.boosters.eyebrow")}</div>
          <h2 className="font-display text-3xl lg:text-4xl text-sol-midnight mb-6">{text("landing.boosters.title")}</h2>
          <p
            data-testid="boosters-purchase-note"
            className="text-sm text-sol-midnight/65 mb-6"
          >
            {text("landing.boosters.purchaseNote")}
          </p>
          <div className="space-y-3">
            {boosters.map((b) => {
              const Icon = BOOSTER_ICONS[b.icon] || FALLBACK_BOOSTER_ICON;
              return (
                <div
                  key={b.booster_id}
                  data-testid={`booster-${b.booster_id}`}
                  className="bg-white border border-sol-midnight/8 rounded-2xl p-5 flex items-start gap-4 hover:shadow-md transition"
                >
                  {b.image_url ? (
                    <img
                      src={platformMediaSrc(b.image_url)}
                      alt=""
                      data-testid={`booster-image-${b.booster_id}`}
                      className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-sol-gold/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-sol-gold" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <h4 className="font-sol font-semibold text-sol-midnight">{b.name}</h4>
                      <div className="font-sol font-semibold text-sol-midnight whitespace-nowrap">
                        +{formatPrice(b.monthly_price?.[currency] || 0, currency)}
                        <span className="text-xs text-sol-midnight/50 font-normal">{text("pricing.perMonth")}</span>
                      </div>
                    </div>
                    <ul className="mt-2 text-xs text-sol-midnight/60 space-y-0.5">
                      {b.features.slice(0, 3).map((f) => (
                        <li key={`b-feat-${b.id || b.name}-${f}`}>· {f}</li>
                      ))}
                    </ul>
                    <PackageCta itemId={b.booster_id} itemName={b.name} currency={currency} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Platform fees */}
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-sol-sage mb-4">{text("landing.fees.eyebrow")}</div>
          <h2 className="font-display text-3xl lg:text-4xl text-sol-midnight mb-8">{text("landing.fees.title")}</h2>
          <div className="space-y-3">
            {platformFees.map((fee) => (
              <div
                key={fee.id}
                data-testid={`fee-${fee.id}`}
                className="bg-sol-midnight rounded-2xl p-5 text-sol-ivory"
              >
                <div className="flex items-baseline justify-between mb-1">
                  <h4 className="font-sol font-semibold">{fee.name}</h4>
                  <div className="text-sol-gold font-mono text-sm font-medium">{fee.rate}</div>
                </div>
                <p className="text-xs text-sol-ivory/55 leading-relaxed">{fee.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
